import axios from 'axios';
import { getToken, clearAuth } from './authToken';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      clearAuth();
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  partnerLogin: (username: string, password: string, remember = false) =>
    api.post('/auth/partner-login', { username, password, remember }),
  changePassword: (data: { current_password: string; new_password: string }) =>
    api.post('/auth/change-password', data),
};

export const partnerAPI = {
  dashboard: () => api.get('/partner/dashboard'),
  schedules: (params?: { keyword?: string; status?: string; test_year?: string }) =>
    api.get('/partner/schedules', { params: { page_size: 500, ...params } }),
  scheduleDetail: (id: number) => api.get(`/partner/schedules/${id}`),
  confirmSchedule: (id: number) => api.post(`/partner/schedules/${id}/confirm`),
  notifications: () => api.get('/partner/notifications'),
  markNotificationRead: (id: number) => api.post(`/partner/notifications/${id}/read`),
  // 문의/요청
  inquiries: (params?: { status?: string; schedule_id?: number }) =>
    api.get('/partner/inquiries', { params }),
  inquirySummary: () => api.get('/partner/inquiries/summary'),
  inquiryScheduleSummary: () => api.get('/partner/inquiries/schedule-summary'),
  inquiryDetail: (id: number) => api.get(`/partner/inquiries/${id}`),
  createInquiry: (data: { schedule_id?: number; inquiry_type: string; title: string; content: string }) =>
    api.post('/partner/inquiries', data),
  replyInquiry: (id: number, content: string) =>
    api.post(`/partner/inquiries/${id}/replies`, { content }),
  uploadInquiryFile: (inquiryId: number, file: File, replyId?: number) => {
    const fd = new FormData();
    fd.append('file', file);
    if (replyId) fd.append('reply_id', String(replyId));
    return api.post(`/partner/inquiries/${inquiryId}/attachments`, fd, { timeout: 120000 });
  },
  downloadInquiryFile: (attachmentId: number) =>
    api.get(`/inquiry-attachments/${attachmentId}/download`, { responseType: 'blob', timeout: 120000 }),
  deleteInquiryFile: (attachmentId: number) => api.delete(`/inquiry-attachments/${attachmentId}`),
  statusCodes: () => api.get('/partner/status-codes'),
  logPageView: (path: string, name: string) =>
    api.post('/partner/activity-log', { path, name }).catch(() => {}),
};

// 병해충 정보 (NCPMS) — 파트너는 조회 전용 (동기화 없음)
export const ncpmsAPI = {
  // 병해충 도감 (실시간 프록시 — 외부 API라 타임아웃 여유)
  search: (keyword: string, page = 1, pageSize = 20) =>
    api.get('/ncpms/search', { params: { keyword, page, page_size: pageSize }, timeout: 45000 }),
  detail: (serviceCode: string, key: string) =>
    api.get('/ncpms/detail', { params: { service_code: serviceCode, key }, timeout: 45000 }),
  // 예찰정보 (로컬 캐시)
  surveys: (params?: { year?: string; crop?: string; kind?: string; page?: number; page_size?: number }) =>
    api.get('/ncpms/surveys', { params }),
  surveyFilters: () => api.get('/ncpms/surveys/filters'),
  surveyDetail: (insectKey: string) => api.get(`/ncpms/surveys/${insectKey}`, { timeout: 45000 }),
  surveyAnalysis: (params: { crop: string; year?: string; survey_kind?: string }) =>
    api.get('/ncpms/surveys/analysis', { params, timeout: 45000 }),
  // 병해충 상담 Q&A (실시간 프록시)
  consults: (page = 1, pageSize = 20) =>
    api.get('/ncpms/consults', { params: { page, page_size: pageSize }, timeout: 45000 }),
  consultDetail: (dgnssReqNo: string) => api.get(`/ncpms/consults/${dgnssReqNo}`, { timeout: 45000 }),
};

export default api;
